import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './ScoreSheetManagement.css';
import { Eye, Trash2, Info, QrCode, MapPin } from 'lucide-react';
import { useZxing } from 'react-zxing';
import { ApiError } from '../../../../../../lib/apiClient';
import { clubsService, eventsService, gameService, rifleService, scoringService } from '../../../../../../services';
import { compareStandsByIdentity } from '../../../../../../pages/dashboard/ManageClubs/standClayConfig';

const clayDisciplineTypes = {
    Skeet: ["EnglishSkeet", "AmericanSkeet", "OlympicSkeet", "OlympicSkeetPre2012", "OlympicSkeetFinal", "SkeetDoubles", "SkeetShootOff", "WobbleSkeet"],
    Sporting: ["CompakSporting", "EnglishSporting", "FITASCSporting", "FiveStand", "Sportrap", "SuperSporting"],
    Trap: ["AmericanTrap", "AmericanTrapDoubles", "DoubleBarrel", "DoubleRise", "DownTheLine", "OlympicDoubleTrap", "OlympicTrap", "SingleBarrel", "WobbleTrap"]
};

const clayScoreSheetTypes = [
    { value: "Competitive", label: "Competitive" },
    { value: "Training", label: "Training" }
];

const rifleDisciplineTypes = {
    TargetRifle: ["ClubCard", "GalleryRifle", "ServiceRifle"],
    FClass: ["FClassOpen", "FTR"],
    Benchrest: ["LightVarmint", "HeavyVarmint"],
    Smallbore: ["Indoor", "Outdoor"],
    LongRange: ["Practice", "Competition"],
    FullBore: ["MatchRifle", "TargetRifle"],
    PRS: ["Production", "Open"]
};

const rifleScoreSheetTypes = [
    { value: "Competition", label: "Competition" },
    { value: "Practice", label: "Practice" },
    { value: "Training", label: "Training" }
];

const gameTypeOptions = ["Driven", "WalkedUp", "Wildfowling", "RoughShooting", "Pigeon", "Stalking"];
const disciplineTypes = clayDisciplineTypes;
const scoreSheetTypes = clayScoreSheetTypes;

const clayVariantLabels = {
    EnglishSkeet: 'English Skeet',
    AmericanSkeet: 'American Skeet',
    OlympicSkeet: 'Olympic Skeet',
    OlympicSkeetPre2012: 'Olympic Skeet (Pre-2012)',
    OlympicSkeetFinal: 'Olympic Skeet Final',
    SkeetDoubles: 'Skeet Doubles',
    SkeetShootOff: 'Skeet Shoot Off',
    WobbleSkeet: 'Wobble Skeet',
    CompakSporting: 'Compak Sporting',
    EnglishSporting: 'English Sporting',
    FITASCSporting: 'FITASC Sporting',
    FiveStand: 'Five Stand',
    Sportrap: 'Sportrap',
    SuperSporting: 'Super Sporting',
    AmericanTrap: 'American Trap',
    AmericanTrapDoubles: 'American Trap Doubles',
    DoubleBarrel: 'Double Barrel',
    DoubleRise: 'Double Rise',
    DownTheLine: 'Down The Line',
    OlympicDoubleTrap: 'Olympic Double Trap',
    OlympicTrap: 'Olympic Trap',
    SingleBarrel: 'Single Barrel',
    WobbleTrap: 'Wobble Trap (ABT)',
    FunShoot: 'Fun Shoot'
};

const getClayVariantLabel = (variant) => clayVariantLabels[variant] || variant;

const getShooterDisplayName = (shooter) =>
    shooter?.shooterName || `${shooter?.firstName || ''} ${shooter?.lastName || ''}`.trim() || shooter?.userName || 'Shooter';

const getShooterInitials = (label) => {
    const parts = String(label || 'SC').split(' ').filter(Boolean).slice(0, 2);
    return parts.map((part) => part[0]?.toUpperCase()).join('') || 'SC';
};

const buildAdhocFunShootClayConfig = (standName, standCount) => {
    const safeStandCount = Math.max(1, Number(standCount) || 1);
    const stands = Array.from({ length: safeStandCount }, (_, index) => ({
        standNumber: index + 1,
        standName: index === 0 ? (standName || '') : '',
        targetLabels: ['A', 'B']
    }));

    return JSON.stringify({
        totalStands: safeStandCount,
        stands
    });
};

const ScoreSheetManagement = ({ eventId, isOpen, onClose, refreshKey = 0, canManage = true, initialStandId = null }) => {
    const navigate = useNavigate();
    const isReadOnly = !canManage;
    const [eventType, setEventType] = useState('Clay');
    const [scoreSheets, setScoreSheets] = useState([]);
    const [gameDay, setGameDay] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [disciplineType, setDisciplineType] = useState('');
    const [variant, setVariant] = useState('');
    const [scoreSheetType, setScoreSheetType] = useState('Competitive');
    const [availableShooters, setAvailableShooters] = useState([]);
    const [selectedShooters, setSelectedShooters] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [stands, setStands] = useState([]);
    const [selectedStand, setSelectedStand] = useState(null);
    const [standName, setStandName] = useState('');
    const [funShootStartMode, setFunShootStartMode] = useState('saved');
    const [adhocStandCount, setAdhocStandCount] = useState(5);
    const [competitiveStandMode, setCompetitiveStandMode] = useState('saved');
    const [showQrScanner, setShowQrScanner] = useState(false);
    const [showStandInfo, setShowStandInfo] = useState(false);
    const [scanError, setScanError] = useState(null);
    const [loadedEventId, setLoadedEventId] = useState(null);

    const [rifleDisciplineType, setRifleDisciplineType] = useState('TargetRifle');
    const [rifleVariant, setRifleVariant] = useState('ClubCard');
    const [rifleScoreSheetType, setRifleScoreSheetType] = useState('Practice');
    const [rifleDistance, setRifleDistance] = useState('');
    const [rifleDistanceUnit, setRifleDistanceUnit] = useState('metres');
    const [rifleNumberOfShots, setRifleNumberOfShots] = useState('10');
    const [riflePosition, setRiflePosition] = useState('Prone');
    const [rifleRangeName, setRifleRangeName] = useState('');
    const [rifleNotes, setRifleNotes] = useState('');

    const [gameType, setGameType] = useState('Driven');
    const [gameEstate, setGameEstate] = useState('');
    const [gameBeat, setGameBeat] = useState('');
    const [gameWeather, setGameWeather] = useState('');
    const [gameNumberOfGuns, setGameNumberOfGuns] = useState('');
    const [gameNumberOfDrives, setGameNumberOfDrives] = useState('');
    const [gameNotes, setGameNotes] = useState('');

    const handleQrScan = useCallback((data) => {
        console.log("QR Data:", data);
        if (!data) return;

        try {
            let standId = '', scannedDisciplineType = '', scannedVariant = '';
            const parts = data.includes('|') ? data.split('|') : [data];

            [standId, scannedDisciplineType, scannedVariant] = parts;

            if (standId && standId.startsWith('http')) {
                const idMatch = standId.match(/\/stand\/(\d+)/);
                if (idMatch && idMatch[1]) {
                    standId = idMatch[1];
                }
            }

            const stand = stands.find(s => s.standId.toString() === standId.toString());
            if (!stand) {
                setScanError('Stand not found for this club.');
                return;
            }

            setSelectedStand(stand);
            setStandName(stand.name);

            if (scannedDisciplineType && clayDisciplineTypes[scannedDisciplineType]) {
                setDisciplineType(scannedDisciplineType);
                if (scannedVariant && clayDisciplineTypes[scannedDisciplineType].includes(scannedVariant)) {
                    setVariant(scannedVariant);
                } else {
                    setVariant(clayDisciplineTypes[scannedDisciplineType][0]);
                }
            }

            setTimeout(() => setShowQrScanner(false), 1000);

        } catch (err) {
            console.error("QR Scan Error:", err);
            setScanError('Invalid QR code format. Expected: standId|disciplineType|variant');
        }
    }, [stands]);

    const { ref } = useZxing({
        onDecodeResult(result) {
            handleQrScan(result.getText());
        },
        onDecodeError(error) {
            if (error.message !== 'No MultiFormat Readers were able to detect the code.') {
                console.error('QR scan error:', error);
                setScanError('Failed to scan QR code. Please try again.');
            }
        },
        constraints: { video: { facingMode: { ideal: 'environment' } } },
        timeBetweenDecodingAttempts: 300
    });

    const requestCameraAccess = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            stream.getTracks().forEach(track => track.stop());
            return true;
        } catch (err) {
            console.error('Camera access denied or unavailable:', err);
            setScanError('Unable to access camera. Please check permissions.');
            return false;
        }
    };

    const handleScanQrCode = async () => {
        const granted = await requestCameraAccess();
        if (granted) {
            setScanError(null);
            setShowQrScanner(true);
        }
    };

    const fetchScoreSheets = useCallback(async () => {
        try {
            const data = eventType === 'Rifle'
                ? await rifleService.getEventScoreSheets(eventId)
                : await scoringService.getEventScoreSheets(eventId);
            setScoreSheets(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
            setError(`Failed to load ${eventType === 'Rifle' ? 'rifle score sheets' : 'score sheets'}.`);
        } finally {
            setIsLoading(false);
        }
    }, [eventId, eventType]);

    const fetchGameDay = useCallback(async () => {
        try {
            const data = await gameService.getEventGameDay(eventId);
            setGameDay(data);
        } catch (err) {
            if (err instanceof ApiError && err.status === 404) {
                setGameDay(null);
                return;
            }
            console.error(err);
            setError('Failed to load game day.');
        } finally {
            setIsLoading(false);
        }
    }, [eventId]);

    const fetchAvailableShooters = useCallback(async () => {
        if (!eventId) return;

        try {
            setIsLoading(true);
            const data = await eventsService.getEventShooters(eventId);
            setAvailableShooters(data);

            console.log(`Fetched ${data.length} shooters for event ${eventId}`);
        } catch (err) {
            console.error(err);
            setError('Failed to load shooters.');
        } finally {
            setIsLoading(false);
        }
    }, [eventId]);

    const fetchClubStands = async (clubId) => {
        try {
            const data = await clubsService.getPublicClubStands(clubId);
            setStands((Array.isArray(data) ? data : []).sort(compareStandsByIdentity));
        } catch (err) {
            console.error('Failed to fetch club stands', err);
        }
    };

    const fetchEventDetails = useCallback(async () => {
        try {
            const data = await eventsService.getEvent(eventId);
            const resolvedEventType = data.eventType || 'Clay';
            setEventType(resolvedEventType);
            if (resolvedEventType === 'Clay' && data.clubId) fetchClubStands(data.clubId);
        } catch (err) {
            console.error('Failed to fetch event details', err);
        }
    }, [eventId]);

    // Reset component state when opening with a new event
    useEffect(() => {
        if (isOpen && (eventId !== loadedEventId || refreshKey)) {
            console.log(`Loading ScoreSheetManagement for event ${eventId}. RefreshKey: ${refreshKey}`);
            setLoadedEventId(eventId);
            setEventType('Clay');
            setDisciplineType('');
            setVariant('');
            setScoreSheetType('Competitive');
            setSelectedShooters([]);
            setSelectedStand(null);
            setStandName('');
            setAdhocStandCount(5);
            setFunShootStartMode('saved');
            setCompetitiveStandMode('saved');
            setStands([]);
            setError(null);
            setScoreSheets([]);
            setGameDay(null);

            if (eventId) {
                setIsLoading(true);
                if (!isReadOnly) {
                    fetchAvailableShooters();
                }
                fetchEventDetails();
            }
        }
    }, [isOpen, eventId, loadedEventId, refreshKey, fetchAvailableShooters, fetchEventDetails, isReadOnly]);

    useEffect(() => {
        if (scoreSheetType !== 'FunShoot') {
            return;
        }

        if (stands.length === 0) {
            setFunShootStartMode('adhoc');
            return;
        }

        if (funShootStartMode !== 'saved' && funShootStartMode !== 'scan' && funShootStartMode !== 'adhoc') {
            setFunShootStartMode('saved');
        }
    }, [scoreSheetType, stands.length, funShootStartMode]);

    useEffect(() => {
        if (scoreSheetType === 'FunShoot') {
            return;
        }

        if (stands.length === 0) {
            setCompetitiveStandMode('manual');
            setSelectedStand(null);
            return;
        }

        if (competitiveStandMode !== 'saved' && competitiveStandMode !== 'manual') {
            setCompetitiveStandMode('saved');
        }
    }, [scoreSheetType, stands.length, competitiveStandMode]);

    useEffect(() => {
        if (!isOpen || !initialStandId || !stands.length) {
            return;
        }

        const matchingStand = stands.find((stand) => String(stand.standId) === String(initialStandId));
        if (!matchingStand) {
            return;
        }

        setSelectedStand(matchingStand);
        setStandName(matchingStand.name || '');
        if (scoreSheetType === 'FunShoot') {
            setFunShootStartMode('saved');
        } else {
            setCompetitiveStandMode('saved');
        }
    }, [initialStandId, isOpen, scoreSheetType, stands]);

    useEffect(() => {
        if (!isOpen || !eventId) return;
        setIsLoading(true);
        if (eventType === 'Game') {
            fetchGameDay();
        } else {
            fetchScoreSheets();
        }
    }, [isOpen, eventId, eventType, fetchScoreSheets, fetchGameDay]);

    const handleCreateScoreSheet = async () => {
        const effectiveDisciplineType = scoreSheetType === 'FunShoot'
            ? (disciplineType || 'Sporting')
            : disciplineType;
        const effectiveVariant = scoreSheetType === 'FunShoot'
            ? 'FunShoot'
            : variant;

        if (!effectiveDisciplineType || !effectiveVariant || selectedShooters.length === 0 || !scoreSheetType) {
            setError('All fields are required.');
            return;
        }

        setIsSubmitting(true);
        try {
            const usesSavedStand = scoreSheetType === 'FunShoot'
                ? funShootStartMode !== 'adhoc'
                : competitiveStandMode === 'saved';
            const adhocFunShootConfig = scoreSheetType === 'FunShoot' && funShootStartMode === 'adhoc'
                ? buildAdhocFunShootClayConfig(standName, adhocStandCount)
                : null;

            const payload = {
                eventId,
                disciplineType: effectiveDisciplineType,
                variant: effectiveVariant,
                scoreSheetType,
                standName,
                clayConfigJson: usesSavedStand ? (selectedStand?.clayConfigJson || null) : adhocFunShootConfig,
                shooterIds: selectedShooters.map(s => s.eventShooterId)
            };
            const data = await scoringService.createScoreSheet(payload);
            const isFunShoot = data.scoreSheetType === 'FunShoot' || data.variant === 'FunShoot';
            await fetchScoreSheets();
            setDisciplineType('');
            setVariant('');
            setScoreSheetType('Competitive');
            setSelectedShooters([]);
            setStandName('');
            setSelectedStand(null);
            if (data?.scoreSheetId) {
                onClose();
                navigate(
                    isFunShoot
                        ? `/dashboard/score/fun-shoot/${data.scoreSheetId}`
                        : `/dashboard/score/score-shoot/${data.scoreSheetId}`
                );
            }
        } catch (err) {
            console.error(err);
            setError('Could not create score sheet.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreateRifleScoreSheet = async () => {
        if (!rifleDisciplineType || selectedShooters.length === 0) {
            setError('Discipline and shooters are required.');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                eventId,
                shooterIds: selectedShooters.map(s => s.eventShooterId),
                disciplineType: rifleDisciplineType,
                variant: rifleVariant || null,
                scoreSheetType: rifleScoreSheetType,
                distance: rifleDistance === '' ? null : Number(rifleDistance),
                distanceUnit: rifleDistanceUnit,
                numberOfShots: rifleNumberOfShots === '' ? null : Number(rifleNumberOfShots),
                position: riflePosition || null,
                rangeName: rifleRangeName || null,
                notes: rifleNotes || null
            };
            await rifleService.createScoreSheet(payload);
            await fetchScoreSheets();
            setSelectedShooters([]);
            setRifleDistance('');
            setRifleRangeName('');
            setRifleNotes('');
        } catch (err) {
            console.error(err);
            setError('Could not create rifle score sheet.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreateGameDay = async () => {
        setIsSubmitting(true);
        try {
            const payload = {
                eventId,
                gameType,
                estate: gameEstate || null,
                beat: gameBeat || null,
                weatherConditions: gameWeather || null,
                numberOfGuns: gameNumberOfGuns === '' ? null : Number(gameNumberOfGuns),
                numberOfDrives: gameNumberOfDrives === '' ? null : Number(gameNumberOfDrives),
                notes: gameNotes || null
            };
            const data = await gameService.createGameDay(payload);
            setGameDay(data);
        } catch (err) {
            console.error(err);
            setError('Could not create game day.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteScoreSheet = async (scoreSheetId) => {
        const isRifle = eventType === 'Rifle';
        if (!window.confirm(`Delete this ${isRifle ? 'rifle score sheet' : 'score sheet'}?`)) return;
        try {
            if (isRifle) {
                await rifleService.deleteScoreSheet(scoreSheetId);
            } else {
                await scoringService.deleteScoreSheet(scoreSheetId);
            }
            setScoreSheets(scoreSheets.filter(s => (s.scoreSheetId || s.rifleScoreSheetId) !== scoreSheetId));
        } catch (err) {
            console.error('Error deleting score sheet:', err);
            setError('Delete failed.');
        }
    };

    const handleStandNameChange = (e) => {
        setStandName(e.target.value);
        if (
            (scoreSheetType === 'FunShoot' && funShootStartMode === 'adhoc')
            || (scoreSheetType !== 'FunShoot' && competitiveStandMode === 'manual')
        ) {
            setSelectedStand(null);
        }
    };

    const handleStandChange = (e) => {
        const stand = stands.find(s => s.standId.toString() === e.target.value);
        if (!stand) {
            console.warn("Dropdown stand not found:", e.target.value);
            return;
        }

        if (scoreSheetType === 'FunShoot') {
            setFunShootStartMode('saved');
        } else {
            setCompetitiveStandMode('saved');
        }
        setSelectedStand(stand);
        setStandName(stand.name);

        const { discipline, variant } = determineDisciplineAndVariant(stand);
        if (discipline) {
            setDisciplineType(discipline);
            setVariant(variant || clayDisciplineTypes[discipline]?.[0] || '');
        }
    };

    const determineDisciplineAndVariant = (stand) => {
        if (stand.discipline && clayDisciplineTypes[stand.discipline]) {
            let standVariant = stand.variant;

            if (standVariant && standVariant.includes(' ')) {
                standVariant = standVariant.split(' ')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join('');
            }

            if (standVariant && clayDisciplineTypes[stand.discipline].includes(standVariant)) {
                return { discipline: stand.discipline, variant: standVariant };
            }

            if (standVariant) {
                const formattedVariant = standVariant.toLowerCase().replace(/\s+/g, '');
                const matchedVariant = clayDisciplineTypes[stand.discipline].find(v =>
                    v.toLowerCase().replace(/\s+/g, '') === formattedVariant
                );

                if (matchedVariant) {
                    return { discipline: stand.discipline, variant: matchedVariant };
                }
            }

            return { discipline: stand.discipline, variant: null };
        }

        const standTypeLower = (stand.standType || '').toLowerCase();
        let discipline = null;
        let variant = null;

        for (const [discType, variants] of Object.entries(clayDisciplineTypes)) {
            for (const variantName of variants) {
                const variantLower = variantName.toLowerCase();
                const variantSpaced = variantName
                    .replace(/([A-Z])/g, ' $1')
                    .trim()
                    .toLowerCase();

                if (standTypeLower.includes(variantLower) || standTypeLower.includes(variantSpaced)) {
                    return { discipline: discType, variant: variantName };
                }
            }
        }

        if (standTypeLower.includes('skeet')) {
            discipline = 'Skeet';
            if (standTypeLower.includes('english')) {
                variant = 'EnglishSkeet';
            } else if (standTypeLower.includes('american')) {
                variant = 'AmericanSkeet';
            } else if (standTypeLower.includes('olympic')) {
                if (standTypeLower.includes('pre2012') || standTypeLower.includes('pre 2012')) {
                    variant = 'OlympicSkeetPre2012';
                } else if (standTypeLower.includes('final')) {
                    variant = 'OlympicSkeetFinal';
                } else {
                    variant = 'OlympicSkeet';
                }
            } else if (standTypeLower.includes('double') || standTypeLower.includes('doubles')) {
                variant = 'SkeetDoubles';
            } else if (standTypeLower.includes('shootoff') || standTypeLower.includes('shoot off')) {
                variant = 'SkeetShootOff';
            } else if (standTypeLower.includes('wobble')) {
                variant = 'WobbleSkeet';
            } else {
                variant = 'EnglishSkeet'; // Default
            }
        } else if (standTypeLower.includes('trap')) {
            discipline = 'Trap';
            if (standTypeLower.includes('down') || standTypeLower.includes('dtl')) {
                variant = 'DownTheLine';
            } else if (standTypeLower.includes('american')) {
                if (standTypeLower.includes('double') || standTypeLower.includes('doubles')) {
                    variant = 'AmericanTrapDoubles';
                } else {
                    variant = 'AmericanTrap';
                }
            } else if (standTypeLower.includes('olympic')) {
                if (standTypeLower.includes('double') || standTypeLower.includes('doubles')) {
                    variant = 'OlympicDoubleTrap';
                } else {
                    variant = 'OlympicTrap';
                }
            } else if (standTypeLower.includes('double barrel')) {
                variant = 'DoubleBarrel';
            } else if (standTypeLower.includes('double rise') || standTypeLower.includes('doublerise')) {
                variant = 'DoubleRise';
            } else if (standTypeLower.includes('single barrel')) {
                variant = 'SingleBarrel';
            } else if (standTypeLower.includes('wobble')) {
                variant = 'WobbleTrap';
            } else {
                variant = 'DownTheLine'; // Default
            }
        } else if (standTypeLower.includes('sporting') || standTypeLower.includes('sport')) {
            discipline = 'Sporting';
            if (standTypeLower.includes('english')) {
                variant = 'EnglishSporting';
            } else if (standTypeLower.includes('compak')) {
                variant = 'CompakSporting';
            } else if (standTypeLower.includes('fitasc')) {
                variant = 'FITASCSporting';
            } else if (standTypeLower.includes('five') || standTypeLower.includes('5 stand')) {
                variant = 'FiveStand';
            } else if (standTypeLower.includes('trap')) {
                variant = 'Sportrap';
            } else if (standTypeLower.includes('super')) {
                variant = 'SuperSporting';
            } else if (standTypeLower.includes('fun')) {
                variant = 'FunShoot';
            } else {
                variant = 'EnglishSporting'; // Default
            }
        }

        return { discipline, variant };
    };

    const handleViewScoreSheet = (sheet) => {
        if (eventType === 'Rifle') {
            navigate(`/dashboard/score/rifle/${sheet.rifleScoreSheetId}`);
            return;
        }
        const isFunShoot = sheet.scoreSheetType === 'FunShoot' || sheet.variant === 'FunShoot';
        navigate(
            isFunShoot
                ? `/dashboard/score/fun-shoot/${sheet.scoreSheetId}`
                : `/dashboard/score/score-shoot/${sheet.scoreSheetId}`
        );
    };

    const switchFunShootMode = (mode) => {
        setFunShootStartMode(mode);
        setScanError(null);
        setShowStandInfo(false);

        if (mode === 'adhoc') {
            setSelectedStand(null);
            setStandName('');
            setAdhocStandCount(5);
            setDisciplineType('Sporting');
            setVariant('FunShoot');
            return;
        }

        if (mode === 'scan') {
            handleScanQrCode();
        }
    };

    const switchCompetitiveStandMode = (mode) => {
        setCompetitiveStandMode(mode);
        setScanError(null);
        setShowStandInfo(false);

        if (mode === 'manual') {
            setSelectedStand(null);
            return;
        }

        if (!selectedStand && stands.length > 0) {
            setStandName(stands[0].name || '');
        }
    };

    if (!isOpen) return null;

    if (eventType === 'Rifle') {
        return (
            <div className="scoresheet-container active">
                <div className="scoresheet-overlay" onClick={onClose}></div>
                <div className="scoresheet-box">
                    <div className="scoresheet-modal-header">
                        <h2>Manage Rifle Scoring</h2>
                    </div>
                    {error && <div className="scoresheet-error-message">{error}</div>}
                    {isReadOnly && (
                        <div className="scoresheet-readonly-note">
                            You can view rifle score sheets for this event because you are listed as a shooter, but only the event owner can create or manage them.
                        </div>
                    )}
                    {!isReadOnly && (
                    <div className="scoresheet-form">
                        <h5>Create Rifle Score Sheet</h5>
                        <div className="scoresheet-form-row scoresheet-discipline-variant-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Discipline:</label>
                                <div className="scoresheet-form-field">
                                    <select
                                        value={rifleDisciplineType}
                                        onChange={(e) => {
                                            const nextDiscipline = e.target.value;
                                            setRifleDisciplineType(nextDiscipline);
                                            setRifleVariant(rifleDisciplineTypes[nextDiscipline]?.[0] || '');
                                        }}
                                        disabled={isSubmitting}
                                    >
                                        {Object.keys(rifleDisciplineTypes).map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Variant:</label>
                                <div className="scoresheet-form-field">
                                    <select value={rifleVariant} onChange={(e) => setRifleVariant(e.target.value)} disabled={isSubmitting}>
                                        {(rifleDisciplineTypes[rifleDisciplineType] || []).map(v => (
                                            <option key={v} value={v}>{v}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Sheet Type:</label>
                                <div className="scoresheet-form-field">
                                    <select value={rifleScoreSheetType} onChange={(e) => setRifleScoreSheetType(e.target.value)} disabled={isSubmitting}>
                                        {rifleScoreSheetTypes.map(type => (
                                            <option key={type.value} value={type.value}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Position:</label>
                                <div className="scoresheet-form-field">
                                    <select value={riflePosition} onChange={(e) => setRiflePosition(e.target.value)} disabled={isSubmitting}>
                                        {['Prone', 'Standing', 'Kneeling', 'Sitting', 'Benchrest'].map(position => (
                                            <option key={position} value={position}>{position}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Distance:</label>
                                <div className="scoresheet-form-field">
                                    <input type="number" value={rifleDistance} onChange={(e) => setRifleDistance(e.target.value)} placeholder="100" disabled={isSubmitting} />
                                </div>
                            </div>
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Unit:</label>
                                <div className="scoresheet-form-field">
                                    <select value={rifleDistanceUnit} onChange={(e) => setRifleDistanceUnit(e.target.value)} disabled={isSubmitting}>
                                        <option value="metres">metres</option>
                                        <option value="yards">yards</option>
                                    </select>
                                </div>
                            </div>
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Shots:</label>
                                <div className="scoresheet-form-field">
                                    <input type="number" value={rifleNumberOfShots} onChange={(e) => setRifleNumberOfShots(e.target.value)} placeholder="10" disabled={isSubmitting} />
                                </div>
                            </div>
                        </div>
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Range Name:</label>
                                <div className="scoresheet-form-field">
                                    <input type="text" value={rifleRangeName} onChange={(e) => setRifleRangeName(e.target.value)} placeholder="Century range" disabled={isSubmitting} />
                                </div>
                            </div>
                        </div>
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Select Shooters (Selected: {selectedShooters.length})</label>
                                <div className="scoresheet-shooters-list">
                                    {availableShooters.map(shooter => (
                                        <div key={shooter.eventShooterId} className="scoresheet-shooter-item">
                                            <div className="scoresheet-form-checkbox">
                                                <input
                                                    id={`rifle-shooter-${shooter.eventShooterId}`}
                                                    type="checkbox"
                                                    checked={selectedShooters.some(s => s.eventShooterId === shooter.eventShooterId)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedShooters([...selectedShooters, shooter]);
                                                        } else {
                                                            setSelectedShooters(selectedShooters.filter(s => s.eventShooterId !== shooter.eventShooterId));
                                                        }
                                                    }}
                                                    disabled={isSubmitting}
                                                />
                                                <label htmlFor={`rifle-shooter-${shooter.eventShooterId}`}>
                                                    <span className="scoresheet-shooter-option">
                                                        {shooter.profilePictureUrl ? (
                                                            <img
                                                                src={shooter.profilePictureUrl}
                                                                alt={getShooterDisplayName(shooter)}
                                                                className="scoresheet-shooter-avatar scoresheet-shooter-avatar-image"
                                                            />
                                                        ) : (
                                                            <span className="scoresheet-shooter-avatar">
                                                                {getShooterInitials(getShooterDisplayName(shooter))}
                                                            </span>
                                                        )}
                                                        <span className="scoresheet-shooter-option-copy">
                                                            {getShooterDisplayName(shooter)}
                                                        </span>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Notes:</label>
                                <div className="scoresheet-form-field">
                                    <input type="text" value={rifleNotes} onChange={(e) => setRifleNotes(e.target.value)} placeholder="Optional notes" disabled={isSubmitting} />
                                </div>
                            </div>
                        </div>
                        <div className="scoresheet-form-actions">
                            <button
                                type="button"
                                className="scoresheet-default-btn scoresheet-create-btn"
                                onClick={handleCreateRifleScoreSheet}
                                disabled={isSubmitting || availableShooters.length === 0 || selectedShooters.length === 0}
                            >
                                {isSubmitting ? 'Creating...' : 'Create Rifle Score Sheet'}
                            </button>
                        </div>
                    </div>
                    )}
                    <div className="scoresheet-existing-scoresheets">
                        <h5>Existing Rifle Score Sheets</h5>
                        {isLoading ? (
                            <div className="scoresheet-loading">Loading rifle score sheets...</div>
                        ) : scoreSheets.length === 0 ? (
                            <div className="scoresheet-no-data">No rifle score sheets available for this event.</div>
                        ) : (
                            <div className="scoresheet-row">
                                {scoreSheets.map(sheet => (
                                    <div className="scoresheet-col-lg-6 scoresheet-card-container" key={sheet.rifleScoreSheetId}>
                                        <div className="scoresheet-card">
                                            <div className="scoresheet-header">
                                                <h6>{sheet.scoreSheetType} - {sheet.disciplineType} - {sheet.variant || 'General'}</h6>
                                                <div className="scoresheet-meta">
                                                    <span>Created: {new Date(sheet.createdAt).toLocaleString()}</span>
                                                </div>
                                            </div>
                                            <div className="scoresheet-card-cta">
                                                <button className="scoresheet-card-outline-btn" onClick={() => handleViewScoreSheet(sheet)}>
                                                    <Eye size={15} className="scoresheet-me-2" />
                                                    View
                                                </button>
                                                {!isReadOnly && (
                                                    <button className="scoresheet-card-default-btn" onClick={() => handleDeleteScoreSheet(sheet.rifleScoreSheetId)}>
                                                        <Trash2 size={15} className="scoresheet-me-2" />
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (eventType === 'Game') {
        return (
            <div className="scoresheet-container active">
                <div className="scoresheet-overlay" onClick={onClose}></div>
                <div className="scoresheet-box">
                    <div className="scoresheet-modal-header">
                        <h2>Manage Game Day</h2>
                    </div>
                    {error && <div className="scoresheet-error-message">{error}</div>}
                    {isReadOnly && (
                        <div className="scoresheet-readonly-note">
                            You can see this game event because you are listed on it, but only the event owner can create or manage the game day.
                        </div>
                    )}
                    {gameDay ? (
                        <div className="scoresheet-existing-scoresheets">
                            <h5>Game Day Ready</h5>
                            <div className="scoresheet-card">
                                <div className="scoresheet-header">
                                    <h6>{gameDay.gameType} - {gameDay.estate || 'Game day'}</h6>
                                    <div className="scoresheet-meta">
                                        <span>Date: {new Date(gameDay.eventDate || gameDay.createdAt).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                <div className="scoresheet-card-cta">
                                    <button className="scoresheet-card-outline-btn" onClick={() => navigate(`/dashboard/score/game/${gameDay.gameDayId}`)}>
                                        <Eye size={15} className="scoresheet-me-2" />
                                        {isReadOnly ? 'View Game Day' : 'Open Game Day'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : !isReadOnly ? (
                        <div className="scoresheet-form">
                            <h5>Create Game Day</h5>
                            <p className="scoresheet-section-note">
                                The event venue remains the main location. Add estate or beat detail here only when the shoot needs extra game-day context.
                            </p>
                            <div className="scoresheet-form-row">
                                <div className="scoresheet-form-group">
                                    <label className="scoresheet-form-label">Game Type:</label>
                                    <div className="scoresheet-form-field">
                                        <select value={gameType} onChange={(e) => setGameType(e.target.value)} disabled={isSubmitting}>
                                            {gameTypeOptions.map(type => (
                                                <option key={type} value={type}>{type}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="scoresheet-form-group">
                                    <label className="scoresheet-form-label">Estate / Shoot Location:</label>
                                    <div className="scoresheet-form-field">
                                        <input type="text" value={gameEstate} onChange={(e) => setGameEstate(e.target.value)} placeholder="Optional estate or location name" disabled={isSubmitting} />
                                    </div>
                                </div>
                            </div>
                            <div className="scoresheet-form-row">
                                <div className="scoresheet-form-group">
                                    <label className="scoresheet-form-label">Beat:</label>
                                    <div className="scoresheet-form-field">
                                        <input type="text" value={gameBeat} onChange={(e) => setGameBeat(e.target.value)} placeholder="Beat" disabled={isSubmitting} />
                                    </div>
                                </div>
                                <div className="scoresheet-form-group">
                                    <label className="scoresheet-form-label">Weather:</label>
                                    <div className="scoresheet-form-field">
                                        <input type="text" value={gameWeather} onChange={(e) => setGameWeather(e.target.value)} placeholder="Weather conditions" disabled={isSubmitting} />
                                    </div>
                                </div>
                            </div>
                            <div className="scoresheet-form-row">
                                <div className="scoresheet-form-group">
                                    <label className="scoresheet-form-label">Number Of Guns:</label>
                                    <div className="scoresheet-form-field">
                                        <input type="number" value={gameNumberOfGuns} onChange={(e) => setGameNumberOfGuns(e.target.value)} placeholder="8" disabled={isSubmitting} />
                                    </div>
                                </div>
                                <div className="scoresheet-form-group">
                                    <label className="scoresheet-form-label">Number Of Drives:</label>
                                    <div className="scoresheet-form-field">
                                        <input type="number" value={gameNumberOfDrives} onChange={(e) => setGameNumberOfDrives(e.target.value)} placeholder="5" disabled={isSubmitting} />
                                    </div>
                                </div>
                            </div>
                            <div className="scoresheet-form-row">
                                <div className="scoresheet-form-group">
                                    <label className="scoresheet-form-label">Notes:</label>
                                    <div className="scoresheet-form-field">
                                        <input type="text" value={gameNotes} onChange={(e) => setGameNotes(e.target.value)} placeholder="Optional notes" disabled={isSubmitting} />
                                    </div>
                                </div>
                            </div>
                            <div className="scoresheet-form-actions">
                                <button type="button" className="scoresheet-default-btn scoresheet-create-btn" onClick={handleCreateGameDay} disabled={isSubmitting}>
                                    {isSubmitting ? 'Creating...' : 'Create Game Day'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="scoresheet-no-data">No game day has been created for this event yet.</div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="scoresheet-container active">
            <div className="scoresheet-overlay" onClick={onClose}></div>
            <div className="scoresheet-box">
                <div className="scoresheet-modal-header">
                    <h2>
                        {eventType === 'Clay'
                            ? 'Start Clay Scoring'
                            : eventType === 'Rifle'
                                ? 'Manage Rifle Score Sheets'
                                : eventType === 'Game'
                                    ? 'Manage Game Day'
                                    : 'Manage Score Sheets'}
                    </h2>
                </div>
                {error && <div className="scoresheet-error-message">{error}</div>}
                {isReadOnly && (
                    <div className="scoresheet-readonly-note">
                        You can view score sheets for this event because you were added as a shooter. Only the event owner can create or manage them.
                    </div>
                )}

                {!isReadOnly && (
                <div className="scoresheet-form">
                    <h5>
                        {eventType === 'Clay'
                            ? 'Set Up This Clay Session'
                            : 'Create New Score Sheet'}
                    </h5>

                    <div className="scoresheet-form-row">
                        <div className="scoresheet-form-group">
                            <label className="scoresheet-form-label">Score Sheet Type:</label>
                            <div className="scoresheet-form-field">
                                <select
                                    value={scoreSheetType}
                                    onChange={(e) => {
                                        const nextType = e.target.value;
                                        setScoreSheetType(nextType);
                                        if (nextType === 'FunShoot') {
                                            setDisciplineType('Sporting');
                                            setVariant('FunShoot');
                                            setFunShootStartMode(stands.length > 0 ? 'saved' : 'adhoc');
                                        }
                                    }}
                                    disabled={isSubmitting}
                                >
                                    {scoreSheetTypes.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    {scoreSheetType === 'FunShoot' && (
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">How are you starting this fun shoot?</label>
                                <div className="scoresheet-funshoot-modes">
                                    {stands.length > 0 && (
                                        <>
                                            <button
                                                type="button"
                                                className={`scoresheet-mode-btn ${funShootStartMode === 'saved' ? 'active' : ''}`}
                                                onClick={() => switchFunShootMode('saved')}
                                                disabled={isSubmitting}
                                            >
                                                Pick Saved Stand
                                            </button>
                                            <button
                                                type="button"
                                                className={`scoresheet-mode-btn ${funShootStartMode === 'scan' ? 'active' : ''}`}
                                                onClick={() => switchFunShootMode('scan')}
                                                disabled={isSubmitting}
                                            >
                                                <QrCode size={16} /> Scan QR
                                            </button>
                                        </>
                                    )}
                                    <button
                                        type="button"
                                        className={`scoresheet-mode-btn ${funShootStartMode === 'adhoc' ? 'active' : ''}`}
                                        onClick={() => switchFunShootMode('adhoc')}
                                        disabled={isSubmitting}
                                    >
                                        Ad Hoc Stand
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {scoreSheetType !== 'FunShoot' && stands.length > 0 && (
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Stand Source:</label>
                                <div className="scoresheet-funshoot-modes">
                                    <button
                                        type="button"
                                        className={`scoresheet-mode-btn ${competitiveStandMode === 'saved' ? 'active' : ''}`}
                                        onClick={() => switchCompetitiveStandMode('saved')}
                                        disabled={isSubmitting}
                                    >
                                        Use Venue Stand
                                    </button>
                                    <button
                                        type="button"
                                        className={`scoresheet-mode-btn ${competitiveStandMode === 'manual' ? 'active' : ''}`}
                                        onClick={() => switchCompetitiveStandMode('manual')}
                                        disabled={isSubmitting}
                                    >
                                        Enter Stand Manually
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {stands.length > 0 && (
                        (scoreSheetType === 'FunShoot' && funShootStartMode !== 'adhoc')
                        || (scoreSheetType !== 'FunShoot' && competitiveStandMode === 'saved')
                    ) && (
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">
                                    {scoreSheetType === 'FunShoot' ? 'Saved Stand:' : 'Select Stand:'}
                                    {scoreSheetType !== 'FunShoot' && (
                                        <button
                                            type="button"
                                            className="scoresheet-default-btn"
                                            onClick={handleScanQrCode}
                                            disabled={isSubmitting}
                                        >
                                            <QrCode size={16} /> Scan QR
                                        </button>
                                    )}
                                </label>
                                <div className="scoresheet-stand-selection-container">
                                    <div className="scoresheet-form-field">
                                        <select
                                            value={selectedStand ? selectedStand.standId : ''}
                                            onChange={handleStandChange}
                                            disabled={isSubmitting}
                                        >
                                            <option value="">-- Select a Stand --</option>
                                            {stands.map(stand => (
                                                <option key={stand.standId} value={stand.standId}>
                                                    {stand.name} ({stand.standType || stand.discipline || 'No type'})
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {selectedStand && (
                                        <button
                                            type="button"
                                            className="scoresheet-default-btn"
                                            onClick={() => setShowStandInfo(!showStandInfo)}
                                        >
                                            <Info size={16} />
                                            <span>{showStandInfo ? 'Hide Info' : 'Info'}</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {(
                        (scoreSheetType === 'FunShoot' && (funShootStartMode === 'adhoc' || stands.length === 0))
                        || (scoreSheetType !== 'FunShoot' && (competitiveStandMode === 'manual' || stands.length === 0))
                    ) && (
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">
                                    {scoreSheetType === 'FunShoot'
                                        ? 'Stand Name Or Note:'
                                        : stands.length > 0
                                            ? 'Stand Name (Manual):'
                                            : 'Stand Name (Optional):'}
                                </label>
                                <div className="scoresheet-form-field">
                                    <input
                                        type="text"
                                        value={standName}
                                        onChange={handleStandNameChange}
                                        placeholder={scoreSheetType === 'FunShoot'
                                            ? 'Tower pair, rabbit bank, crossing left-right...'
                                            : 'Enter stand name'}
                                        disabled={isSubmitting}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    {scoreSheetType === 'FunShoot' && funShootStartMode === 'adhoc' && (
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <label className="scoresheet-form-label">Planned Stand Count:</label>
                                <div className="scoresheet-form-field">
                                    <input
                                        type="number"
                                        min="1"
                                        max="50"
                                        value={adhocStandCount}
                                        onChange={(e) => setAdhocStandCount(Math.max(1, Number(e.target.value) || 1))}
                                        disabled={isSubmitting}
                                    />
                                </div>
                                <div className="admin-form-helper">
                                    Choose how many stands to show initially. You can still add more stands while scoring.
                                </div>
                            </div>
                        </div>
                    )}

                    {showStandInfo && selectedStand && (
                        <div className="scoresheet-stand-info-panel">
                            <h6>{selectedStand.name} - {selectedStand.standType || selectedStand.discipline || 'No type'}</h6>
                            {selectedStand.description && <p>{selectedStand.description}</p>}

                            {selectedStand.photos && selectedStand.photos.length > 0 && (
                                <div className="scoresheet-stand-photos">
                                    <h6>Photos</h6>
                                    <div className="scoresheet-photo-grid">
                                        {selectedStand.photos.map((photo, index) => (
                                            <div key={`photo-${index}`} className="scoresheet-stand-photo">
                                                <img src={photo.photoUrl} alt={photo.description || 'Stand photo'} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="scoresheet-form-row scoresheet-discipline-variant-row">
                        <div className="scoresheet-form-group">
                            <label className="scoresheet-form-label">Discipline:</label>
                            <div className="scoresheet-form-field">
                                <select
                                    value={disciplineType}
                                    onChange={(e) => {
                                        setDisciplineType(e.target.value);
                                        setVariant('');
                                    }}
                                    disabled={isSubmitting || scoreSheetType === 'FunShoot'}
                                >
                                    <option value="">Select Discipline</option>
                                    {Object.keys(disciplineTypes).map(type => (
                                        <option key={type} value={type}>{type}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="scoresheet-form-group">
                            <label className="scoresheet-form-label">Variant:</label>
                            <div className="scoresheet-form-field">
                                <select
                                    value={scoreSheetType === 'FunShoot' ? 'FunShoot' : variant}
                                    onChange={(e) => setVariant(e.target.value)}
                                    disabled={!disciplineType || isSubmitting || scoreSheetType === 'FunShoot'}
                                >
                                    <option value="">Select Variant</option>
                                    {scoreSheetType === 'FunShoot'
                                        ? <option value="FunShoot">FunShoot</option>
                                        : disciplineType && disciplineTypes[disciplineType].map(v => (
                                        <option key={v} value={v}>{getClayVariantLabel(v)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    {scoreSheetType === 'FunShoot' && (
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <div className="admin-form-helper">
                                    Fun shoot is stand-first. Start from a saved stand, a QR code, or an ad hoc stand note, then jump straight into the live scorer and record each shooter stand by stand.
                                </div>
                            </div>
                        </div>
                    )}
                    {scoreSheetType !== 'FunShoot' && (
                        <div className="scoresheet-form-row">
                            <div className="scoresheet-form-group">
                                <div className="admin-form-helper">
                                    Competitive clay opens straight into the scoring card once created so you can start scoring the planned sequence immediately.
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="scoresheet-form-row">
                        <div className="scoresheet-form-group">
                            <label className="scoresheet-form-label">
                                Select Shooters (Selected: {selectedShooters.length})
                            </label>
                            {availableShooters.length === 0 && (
                                <div className="scoresheet-error-message">
                                    Add shooters to the event first, then create a score sheet.
                                </div>
                            )}
                            <div className="scoresheet-shooters-list">
                                {availableShooters.map(shooter => (
                                    <div key={shooter.eventShooterId} className="scoresheet-shooter-item">
                                        <div className="scoresheet-form-checkbox">
                                            <input
                                                id={`shooter-${shooter.eventShooterId}`}
                                                type="checkbox"
                                                checked={selectedShooters.some(s => s.eventShooterId === shooter.eventShooterId)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedShooters([...selectedShooters, shooter]);
                                                    } else {
                                                        setSelectedShooters(selectedShooters.filter(
                                                            s => s.eventShooterId !== shooter.eventShooterId
                                                        ));
                                                    }
                                                }}
                                                disabled={isSubmitting}
                                            />
                                            <label htmlFor={`shooter-${shooter.eventShooterId}`}>
                                                <span className="scoresheet-shooter-option">
                                                    {shooter.profilePictureUrl ? (
                                                        <img
                                                            src={shooter.profilePictureUrl}
                                                            alt={getShooterDisplayName(shooter)}
                                                            className="scoresheet-shooter-avatar scoresheet-shooter-avatar-image"
                                                        />
                                                    ) : (
                                                        <span className="scoresheet-shooter-avatar">
                                                            {getShooterInitials(getShooterDisplayName(shooter))}
                                                        </span>
                                                    )}
                                                    <span className="scoresheet-shooter-option-copy">
                                                        {getShooterDisplayName(shooter)}
                                                    </span>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="scoresheet-form-actions">
                        <button
                            type="button"
                            className="scoresheet-default-btn scoresheet-create-btn"
                            onClick={handleCreateScoreSheet}
                            disabled={isSubmitting || availableShooters.length === 0 || selectedShooters.length === 0}
                        >
                            {isSubmitting
                                ? 'Creating...'
                                : scoreSheetType === 'FunShoot'
                                    ? 'Start Fun Shoot'
                                    : 'Create Score Sheet'}
                        </button>
                    </div>
                </div>
                )}
                {showQrScanner && (
                    <div className="scoresheet-qr-modal-overlay active" onClick={() => setShowQrScanner(false)}>
                        <div className="scoresheet-qr-scanner-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="scoresheet-qr-scanner-header">
                                <h5>Scan Stand QR Code</h5>
                                <button className="scoresheet-close-btn" onClick={() => setShowQrScanner(false)}>x</button>
                            </div>
                            {scanError && <div className="scoresheet-error-message">{scanError}</div>}
                            <div className="scoresheet-qr-scanner-container">
                                <video key={showQrScanner ? 'scanner-on' : 'scanner-off'} ref={ref} className="scoresheet-qr-video" autoPlay playsInline muted />
                            </div>
                            <div className="scoresheet-qr-scanner-footer">
                                <button className="scoresheet-outline-btn" onClick={() => setShowQrScanner(false)}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}
                <div className="scoresheet-existing-scoresheets">
                    <h5>Existing Score Sheets</h5>
                    {isLoading ? (
                        <div className="scoresheet-loading">Loading score sheets...</div>
                    ) : scoreSheets.length === 0 ? (
                        <div className="scoresheet-no-data">No score sheets available for this event.</div>
                    ) : (
                        <div className="scoresheet-row">
                            {scoreSheets.map(sheet => (
                                <div className="scoresheet-col-lg-6 scoresheet-card-container" key={sheet.scoreSheetId}>
                                    <div className="scoresheet-card">
                                        <div className="scoresheet-header">
                                            <h6>{sheet.scoreSheetType} - {sheet.disciplineType} - {sheet.variant}</h6>
                                            <div className="scoresheet-meta">
                                                <span>Created: {new Date(sheet.createdAt).toLocaleString()}</span>
                                                <div className={`scoresheet-status-badge ${sheet.verifiedBy ? 'final' : 'live'}`}>
                                                    {sheet.verifiedBy ? `Finalised by ${sheet.verifiedBy}` : 'Live scoring'}
                                                </div>
                                                {sheet.standName && (
                                                    <div className="scoresheet-stand-badge">
                                                        <MapPin size={14} />
                                                        <span>{sheet.standName}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="scoresheet-card-cta">
                                            <button
                                                className="scoresheet-card-outline-btn"
                                                onClick={() => handleViewScoreSheet(sheet)}
                                            >
                                                <Eye size={15} className="scoresheet-me-2" />
                                                {isReadOnly ? 'View' : 'View'}
                                            </button>
                                            {!isReadOnly && (
                                                <button
                                                    className="scoresheet-card-default-btn"
                                                    onClick={() => handleDeleteScoreSheet(sheet.scoreSheetId)}
                                                >
                                                    <Trash2 size={15} className="scoresheet-me-2" />
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScoreSheetManagement;
